import React, { useContext, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DischargedContext } from "../../../context/DischargedContext";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { getStatusStyles } from "../../../common/helpers";
import useClinicalLocations from "../../../hooks/useClinicalLocations";
import { useObservationsModal } from "../../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../../components/Advanced/DataGrid/observationsColumn";

const DischargedTable = ({ selectedLocation, setSelectedLocation }) => {
  const { t } = useTranslation();
  const { showObservationsModal } = useObservationsModal();
  const { locations: clinicalLocations } = useClinicalLocations();
  const { dischargedPatients, loading, error, setLocationId } = useContext(DischargedContext);
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  useEffect(() => {
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [selectedLocation]);

  const handleLocationChange = (locationId) => {
    setSelectedLocation(locationId);
    setLocationId(locationId === "" ? null : locationId);
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading data.</Typography>;

  const handleViewPatient = (cipId, row) => {
    navigate(`/patient-info/${cipId}`, {
      state: { patientRow: row, returnPath: location.pathname },
    });
  };

  const columns = [
    getObservationsColumn(showObservationsModal, 5, {
      getCipId: (row) => row.varCipId,
      getPatientName: (row) => row.memberFullName || "",
    }),
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View Patient Info">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPatient(params.row.varCipId, params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: t("Main.DischargedPatientsSection.Patients.Status"),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const daysPassed = Math.floor(
          (new Date() - new Date(params.row.dischargeDate)) /
            (1000 * 60 * 60 * 24)
        );
        const { color, iconColor } = getStatusStyles(daysPassed);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color }}>
            <WarningIcon sx={{ color: iconColor }} />
            <span>{params.row.status}</span>
          </Box>
        );
      },
    },
    { field: "memberId", headerName: t("Main.DischargedPatientsSection.Patients.MemberID"), flex: 1, minWidth: 150 },
    {
      field: "memberFullName",
      headerName: t("Main.DischargedPatientsSection.Patients.PatientName"),
      flex: 2,
      minWidth: 300,
    },
    { field: "memberPhone", headerName: t("Main.DischargedPatientsSection.Patients.Phone"), flex: 1, minWidth: 200 },
    { field: "memberMobile", headerName: t("Main.DischargedPatientsSection.Patients.Mobile"), flex: 1, minWidth: 200 },
    {
      field: "dischargeDate",
      headerName: t("Main.DischargedPatientsSection.Patients.DischargeDate"),
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => (params ? params.split("T")[0] : "N/A"),
    },
    { field: "type", headerName: t("Main.DischargedPatientsSection.Patients.Type"), flex: 1, minWidth: 100 },
    {
      field: "updateDate",
      headerName: t("Main.DischargedPatientsSection.Patients.LastUpdate"),
      flex: 1,
      minWidth: 200,
      valueGetter: (params) =>
        params?.row?.updateDate
          ? params.row.updateDate.split("T")[0]
          : "N/A",
    },
    {
      field: "medicalCenter",
      headerName: t("Main.DischargedPatientsSection.Patients.MedicalCenter"),
      flex: 1.5,
      minWidth: 200,
    },
  ];

  return (
    <>
      {/* Location Filter */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          mb: 3,
          px: 1
        }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="location-select-label">Filter by Location</InputLabel>
          <Select
            labelId="location-select-label"
            value={selectedLocation}
            label="Filter by Location"
            onChange={(e) => handleLocationChange(e.target.value)}
          >
            <MenuItem value="">All Locations</MenuItem>
            {clinicalLocations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Patient Table */}
      <div ref={tableRef} style={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={dischargedPatients.map((p, index) => ({
            ...p,
            id: index + 1,
          }))}
          columns={columns}
          pageSize={5}
        />
      </div>
    </>
  );
};

export default DischargedTable;
